/**
 * LunarEventRow — one upcoming lunar event (new/full moon, eclipse, supermoon)
 * on the shared `.transit-row` surface (verbatim classes from TransitRow).
 *
 * `kind` / `sign` arrive as ASCII slugs (services/api/.../routers/lunar.py);
 * mapped here to the platform RU labels (mirrors apps/webapp/.../lunar/copy.ts).
 */
import type { ReactElement } from "react";

import type { EventKind } from "@/lib/lunar-api";

const EVENT_LABELS: Readonly<Record<EventKind, string>> = {
  "new-moon": "Новолуние",
  "full-moon": "Полнолуние",
  "solar-eclipse": "Солнечное затмение",
  "lunar-eclipse": "Лунное затмение",
  "super-moon": "Суперлуние",
};

const EVENT_GLYPHS: Readonly<Record<EventKind, string>> = {
  "new-moon": "●",
  "full-moon": "○",
  "solar-eclipse": "☉",
  "lunar-eclipse": "☾",
  "super-moon": "◎",
};

const SIGN_LABELS: Readonly<Record<string, string>> = {
  aries: "Овен",
  taurus: "Телец",
  gemini: "Близнецы",
  cancer: "Рак",
  leo: "Лев",
  virgo: "Дева",
  libra: "Весы",
  scorpio: "Скорпион",
  sagittarius: "Стрелец",
  capricorn: "Козерог",
  aquarius: "Водолей",
  pisces: "Рыбы",
};

function signLabel(sign: string): string {
  return SIGN_LABELS[sign] ?? sign;
}

const DATE_FMT = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  hour: "2-digit",
  minute: "2-digit",
});

/** "12 мая, 19:56" from an ISO instant; falls back to the raw string. */
function formatMoment(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return DATE_FMT.format(d);
}

export interface LunarEventRowProps {
  readonly kind: EventKind;
  readonly moment: string;
  readonly sign: string;
}

export function LunarEventRow({ kind, moment, sign }: LunarEventRowProps): ReactElement {
  return (
    <div className="transit-row">
      <span className="t-icon" aria-hidden="true">
        {EVENT_GLYPHS[kind]}
      </span>
      <div className="info">
        <div className="t">
          {EVENT_LABELS[kind]} в знаке {signLabel(sign)}
        </div>
        <div className="s">{formatMoment(moment)}</div>
      </div>
    </div>
  );
}
